import type { FormField } from './form';

export interface NetworkStudy {
  title: string;
  uri: string;
  longitudinal: boolean;
  org: string;
  b64Identifier: string;
  avatar: string;
  creation: string;
  archivedAt: string;
  flags?: StudyFlags;
}

export interface StudyFlags {
  spRedirect: boolean;
  importExportStudy: boolean;
  customDataDictionary?: string;
  preferences?: StudyPreferences;
  entryCompletionLogic?: EntryCompletionLogic;
  disableInteractiveMode?: boolean;
  cfrPart11?: boolean;
  appVersion?: string;
  entrySaveButtonText?: string;
  allowDuplicateCaseNames?: boolean;
  showSaveEntryButtonAtBottom?: boolean;
  enableEntryMetrics?: boolean;
  enableStudyLinkManagement?: boolean;
  caseNameLogic?: CaseNameLogic;
  cachePublicEntry?: boolean;
  pumpQcReports?: PumpQcReports;
  /** Turns on the Perfusion Companion features: the scan QR code and the captured scans panel. */
  perfusionCompanion?: boolean;
  // Custom message shown when a form has data entry closed. Falls back to a default when empty.
  closedFormMessage?: string;
  showICData?: boolean;
  /**
   * Turns on the per-case staffing and notification roster, and carries this
   * study's own role vocabulary. See `CaseTeamConfig`.
   */
  caseTeam?: CaseTeamConfig;
  /**
   * Case-scoped notification rules, addressed at the `caseTeam` roles.
   *
   * Read-only here for now: the flag is authored in the admin panel's raw JSON
   * flags editor, and nothing in the EDC writes it. It is declared so the EDC can
   * tell whether a study has rules configured — a case team roster means
   * something different when notifications actually hang off it.
   *
   * The shape mirrors `case.notifications.ts` in spdata-api, which is the authority.
   * Only the parts the EDC needs are spelled out; a rule carries more (content,
   * navigation, conditions) that the API validates and the EDC never reads.
   */
  caseNotifications?: CaseNotificationsConfig;
}

/** One rule's trigger kind. Mirrors spdata-api's `RuleTrigger` discriminator. */
export type CaseNotificationTrigger = 'answer' | 'offset' | 'clock' | 'sweep';

export interface CaseNotificationRule {
  key: string;
  label?: string;
  enable?: boolean;
  action?: string;
  audience?: { roles?: string[]; includeSubscribers?: boolean };
  when?: { on?: CaseNotificationTrigger };
  channels?: { channel?: 'push' | 'email' }[];
}

export interface CaseNotificationAnchor {
  key: string;
  label?: string;
  opensCase?: boolean;
  closesCase?: boolean;
}

export interface CaseNotificationsConfig {
  version?: number;
  enable: boolean;
  timezone?: string;
  anchors?: CaseNotificationAnchor[];
  rules?: CaseNotificationRule[];
}

export interface CaseTeamRole {
  /** Stored verbatim as the member's `roleKey`, e.g. 'lead_surgeon'. */
  key: string;
  /** What the UI shows, e.g. 'Lead Surgeon'. */
  label: string;
  /** True renders a multi-select and permits many holders; default is one. */
  multiple?: boolean;
  /** Shown as a hint in the creation dialog; the API does not enforce it. */
  required?: boolean;
}

/**
 * Per-study config for the case team.
 *
 * The role list and both labels are study-level rather than hardcoded because
 * the vocabulary is clinical, not platform: the pump study labels this "Care
 * Team" with surgeon/perfusionist roles, while the platform default is the
 * neutral "Case Team". Anything reading these should fall back to
 * `DEFAULT_CASE_TEAM_LABEL` / `DEFAULT_CASE_TEAM_SUBSCRIBERS_LABEL` rather than
 * assuming they are set.
 */
export interface CaseTeamConfig {
  enable: boolean;
  label?: string;
  subscribersLabel?: string;
  roles: CaseTeamRole[];
}

export const DEFAULT_CASE_TEAM_LABEL = 'Case Team';
export const DEFAULT_CASE_TEAM_SUBSCRIBERS_LABEL =
  'Additional Notification Recipients';

interface PumpQcReports {
  enable: boolean;
}

interface CaseNameLogic {
  incremental: {
    prefix: string;
    defaultStart?: number;
  };
  suffix: {
    formId: string;
    fieldId: string;
    connector: string;
  };
}

interface StudyPreferences {
  dataViewFilter?: string;
}

interface EntryCompletionLogic {
  requirementEffect: string[]
}

interface IStudy {
  identifier: string;
  b64Identifier: string;
  title: string;
  longitudinal: boolean;
  organizationIdentifier: string;
  avatar: string;
  creation: string;
  archivedAt: string;

  flags?: StudyFlags;
}

export interface INewStudy {
  long: string;
  organizationIdentifier?: string;
  title: string;
}

export interface StudySchemaExport {
  formIdentifiers?: string[];
  flatHumanReadableOptions?: boolean;
}

export interface StudyChanges {
  identifier?: string;
  title?: string;
  description?: string;
  isCaseManagement?: boolean;
  entryCreatedWebhook?: string;
  entryUpdatedWebhook?: string;
  enforcements?: string;
}

interface StudyConfigChanges {
  previous: StudyChanges;
  changes: StudyChanges;
  errors: StudyChanges;
}

interface FieldUpdates {
  fieldsDeleted: number;
  fieldsAdded: number;
  fieldsChanged: number;
}

export interface FormChanges {
  previous: StudyChanges;
  changes: StudyChanges;
  errors: StudyChanges;
  fieldUpdates: FieldUpdates;
}

export interface FormFieldMoved {
  current?: number;
  previous?: number;
}

export interface FormFieldChanges {
  newField?: boolean;
  moved?: FormFieldMoved,
  deleted?: boolean;
}

export interface FormConfig {
  formChanges: FormChanges;
  items: FormField[];
}

export interface StudyImportDiff {
  studyConfigChanges: StudyConfigChanges;
  forms: FormConfig[];
}

export class StudyModel {
  identifier: string;
  b64Identifier: string;
  title: string;

  //longitudinal true means case management ON
  longitudinal: boolean;
  organizationIdentifier: string;
  avatar: string;
  creation: string;
  archivedAt: string;

  flags?: StudyFlags;

  constructor({
    identifier,
    b64Identifier,
    title,
    longitudinal,
    organizationIdentifier,
    avatar,
    creation,
    archivedAt,
    flags
  }: IStudy) {
    this.identifier = identifier;
    this.b64Identifier = b64Identifier;
    this.title = title;
    this.longitudinal = longitudinal;
    this.organizationIdentifier = organizationIdentifier;
    this.avatar = avatar;
    this.creation = creation;
    this.archivedAt = archivedAt;
    this.flags = flags;
  }

  static mapFromBackend(networkStudy: NetworkStudy) {
    return new StudyModel({
      identifier: networkStudy.uri.split('/')[1],
      b64Identifier: networkStudy.b64Identifier,
      title: networkStudy.title,
      longitudinal: networkStudy.longitudinal,
      organizationIdentifier: networkStudy.org,
      avatar: networkStudy.avatar,
      creation: networkStudy.creation,
      archivedAt: networkStudy.archivedAt,
      flags: networkStudy.flags
    });
  }

  static createEmpty() {
    return new StudyModel({
      identifier: '',
      b64Identifier: '',
      title: '',
      longitudinal: true,
      organizationIdentifier: '',
      avatar: '',
      creation: '',
      archivedAt: ''
    });
  }
}
